import { ref } from "vue";
import { defineStore } from "pinia";

/** One working-state `project_model` row, narrowed to the Speckle link the 3D viewer reads. */
export interface ProjectModelRow {
  name: string;
  active: boolean;
  referencedObjectId: string | null;
  commitId: string | null;
  speckleModelId: string | null;
}

/**
 * The WORKING STATE's model rows, kept live (RTLCA-5764).
 *
 * These rows move on their own: a connector push reaches `referenced_object_id` through the commit
 * webhook within seconds and with no page action. `referencedObjectId` is the object the 3D viewer
 * loads — it IS the geometry — and the project wrapper every model read used to go through is only
 * refetched on navigation or a revision switch. So with the viewer open, a push left it holding a
 * superseded Speckle object, and previewing an older revision and coming back still showed the
 * geometry from before the push until the page was reloaded.
 *
 * A plain holder, deliberately: it owns no query. `useProjectModelsLive`, installed once by
 * `ProjectDetailLayout`, feeds it from the `GetProjectModels` subscription — one socket for every
 * reader, the same arrangement the current-revision pointer has.
 *
 * Only the live half lives here. A frozen revision's rows travel with the revision list
 * (`readModelsFor`): they cannot move once frozen, so there is nothing to keep live.
 *
 * `rowsFor` returns null until the first emission lands, and null means "nothing to say" — readers
 * fall back to the wrapper's copy, which is what the app read before this existed, so the viewer
 * still works on a cold load and in an environment where the subscription never connects.
 */
export const useProjectModelsStore = defineStore("projectModels", () => {
  const rows = ref<ProjectModelRow[] | null>(null);
  const projectId = ref<string | null>(null);

  const rowsFor = (id: string): ProjectModelRow[] | null => (projectId.value === id ? rows.value : null);

  /**
   * Replaces the rows for a project. Whole-list, not per row: the subscription emits the project's
   * full working set every time, and a merge would have to guess at removals.
   */
  const applyRows = (id: string, next: ProjectModelRow[]): void => {
    projectId.value = id;
    rows.value = next;
  };

  /**
   * Drops what is held. Called when the subscribed project changes, so a moment of the previous
   * project's geometry can never be resolved against the new one — `rowsFor` is keyed on the id, so
   * this is belt and braces rather than the only guard.
   */
  const reset = (): void => {
    projectId.value = null;
    rows.value = null;
  };

  return { rows, projectId, rowsFor, applyRows, reset };
});

export default useProjectModelsStore;
